import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Select, { components, DropdownIndicatorProps, OptionProps } from 'react-select';
import Icon from '@components/icon/Icon';
import { mdiMagnify } from '@mdi/js';
import classNames from 'classnames';
import { emailRegex } from '@src/utils/regex';
import { User } from '@api/services/profile/types';
import { useUserProfile } from '@hooks';
import './userSearch.scss';

interface OptionType {
  label: string;
  value: string;
  user: User;
}

interface UserSearchProps {
  users: User[];
  onUserSelect: (user: User | null) => void;
  isLoading: boolean;
  onSearchInputChange: (input: string) => void;
  className?: string;
  errorMessage?: string | null;
}

const CustomOption = (props: OptionProps<OptionType, false>) => {
  const { user } = props.data;
  return (
    <components.Option {...props}>
      <div className='user-search__option'>
        <div className='user-search__name'>
          {user.firstName} {user.lastName}
        </div>
        <div className='user-search__email'>{user.emailAddress}</div>
      </div>
    </components.Option>
  );
};

const DropdownIndicator = (props: DropdownIndicatorProps<OptionType, false>) => (
  <components.DropdownIndicator {...props}>
    <div className='user-search__icon-button'>
      <Icon className='user-search__icon' focusable={false} path={mdiMagnify} size={0.8} />
    </div>
  </components.DropdownIndicator>
);

const getUserOptions = (users: User[], currentUserEmail: string): OptionType[] =>
  users
    .filter((user) => user?.emailAddress.toLowerCase() !== currentUserEmail.toLowerCase())
    .map((user) => ({
      label: user?.emailAddress,
      value: user?.emailAddress,
      user,
    }));

const getNoOptionsMessage = (
  inputValue: string,
  currentUserEmail: string,
  errorMessage?: string | null
): string | null => {
  if (!inputValue) return null;
  if (!emailRegex.test(inputValue)) return 'Enter a valid email address';
  if (inputValue.toLowerCase() === currentUserEmail.toLowerCase()) {
    return "You can't add yourself.";
  }
  return errorMessage || 'The user does not exist.';
};

const UserSearch: React.FC<UserSearchProps> = ({
  users,
  onUserSelect,
  isLoading,
  onSearchInputChange,
  className,
  errorMessage,
}) => {
  const containerClass = classNames('user-search', className);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const { userProfileData } = useUserProfile();
  const currentUserEmail = userProfileData?.emailAddress || '';
  const options = useMemo(() => getUserOptions(users, currentUserEmail), [users, currentUserEmail]);

  const getCurrentOptions = useCallback(() => {
    if (options.some((option) => option.value?.toLowerCase() === inputValue?.toLowerCase())) {
      return options;
    } else {
      return [];
    }
  }, [options, inputValue]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedInput(inputValue), 500);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  useEffect(() => {
    const trimmed = debouncedInput.trim();
    if (emailRegex.test(trimmed)) {
      onSearchInputChange(trimmed);
    }
  }, [debouncedInput, onSearchInputChange]);

  return (
    <div className={containerClass}>
      <Select<OptionType, false>
        classNamePrefix='react-select'
        components={{ DropdownIndicator, Option: CustomOption }}
        getOptionLabel={(e) => e.label}
        getOptionValue={(e) => e.value}
        isClearable
        isLoading={isLoading}
        inputId='user-search'
        inputValue={inputValue}
        onChange={(selectedOption) => {
          onUserSelect(selectedOption?.user || null);
          setInputValue('');
        }}
        onInputChange={(input) => {
          setInputValue(input?.trim());
          return input;
        }}
        options={getCurrentOptions()}
        noOptionsMessage={({ inputValue }) => getNoOptionsMessage(inputValue, currentUserEmail, errorMessage)}
        placeholder='Enter an exact email address...'
        styles={{ container: (provided) => ({ ...provided, zIndex: '9999' }) }}
        menuIsOpen={inputValue ? undefined : false}
        value={null}
      />
    </div>
  );
};
export default UserSearch;
